import {
  cancelUploadJob as persistUploadCancellation,
  completeUploadJob as persistUploadCompletion,
  createUploadJob as persistUploadJob,
  listIncompleteUploadJobs,
  normalizeIncompleteUploadJobs,
  updateUploadJob
} from "./uploadPersistence";
import { createUploadJobSession } from "./uploadSession";

function assertJobId(jobId) {
  if (!jobId) throw new Error("An upload job id is required.");
}

export function createUploadManager({
  db,
  sessionFactory = createUploadJobSession,
  taskFactory,
  incompleteJobsLoader = listIncompleteUploadJobs,
  incompleteJobsNormalizer = normalizeIncompleteUploadJobs
} = {}) {
  if (!db) throw new Error("An upload persistence database is required.");

  const activeSessions = new Map();

  function createSession(jobId) {
    return sessionFactory({ db, jobId, taskFactory });
  }

  function trackRun(record, runPromise) {
    const trackedPromise = Promise.resolve(runPromise).finally(() => {
      if (activeSessions.get(record.jobId) === record) {
        activeSessions.delete(record.jobId);
      }
    });
    record.runPromise = trackedPromise;
    return record;
  }

  function startSession(jobId, method) {
    const session = createSession(jobId);
    const record = { jobId, session, runPromise: null };
    activeSessions.set(jobId, record);
    return trackRun(record, session[method]());
  }

  return {
    async registerUpload({ job, sources }) {
      if (!job || !job.id) throw new Error("Upload job must include an id.");
      await persistUploadJob({ db, job, sources });
      return job;
    },

    async startUpload({ job, sources }) {
      if (!job || !job.id) throw new Error("Upload job must include an id.");
      await persistUploadJob({ db, job, sources });
      return startSession(job.id, "start");
    },

    async pauseUpload({ jobId }) {
      assertJobId(jobId);
      const record = activeSessions.get(jobId);
      if (record) {
        await Promise.resolve(record.session.pause());
      }

      const jobs = await incompleteJobsLoader({ db });
      const job = jobs.find(candidate => candidate.id === jobId);
      if (!job) return null;
      if (job.status === "paused") return job;

      const pausedJob = {
        ...job,
        status: "paused",
        updatedAt: new Date().toISOString()
      };
      await updateUploadJob({ db, job: pausedJob });
      return pausedJob;
    },

    resumeUpload({ jobId }) {
      assertJobId(jobId);
      const record = activeSessions.get(jobId);
      if (record) {
        record.session.resume();
        return record;
      }
      return startSession(jobId, "resume");
    },

    async cancelUpload({ jobId }) {
      assertJobId(jobId);
      const record = activeSessions.get(jobId);
      if (record) {
        await Promise.resolve(record.session.cancel());
        activeSessions.delete(jobId);
      }
      return persistUploadCancellation({ db, jobId });
    },

    async finalizeUpload({ jobId }) {
      assertJobId(jobId);
      return persistUploadCompletion({ db, jobId });
    },

    getActiveUploads() {
      return Array.from(activeSessions.values());
    },

    async listIncompleteUploads() {
      await incompleteJobsNormalizer({ db });
      return incompleteJobsLoader({ db });
    },

    readIncompleteUploads() {
      return incompleteJobsLoader({ db });
    }
  };
}
